import React, {forwardRef, useImperativeHandle, useLayoutEffect, useRef, useState} from 'react';
import {Button, Dialog, NavBar} from 'antd-mobile';
import {toCanvas} from 'html-to-image';
import {getBottomNavBarContainerStyle, getNavBarContainerStyle} from "../../../utils/deviceUtils";
import {t} from "../../../i18n";
import SignaturePad from "../../../components/SignaturePad";
import {SWIFTX_LOGO_BASE64} from "../../../assets/images/swiftxLogoBase64";

// 单据双方信息
export interface PickUpBolParty {
    // 提货方为站点ID，收货方为 HUB 代码
    code: string;
    address: string;
    contact: string;
}

export type PickUpBolSignatureRole = 'shipper' | 'driver';

interface PickUpBolProps {
    visible: boolean;
    onClose: () => void;
    bolNumber: string;
    date: string;
    pickup: PickUpBolParty;
    consignee: PickUpBolParty;
    // 货物明细（揽收成功用；传 exceptionReason 时该区块被替换，可不传）
    container?: string;
    quantity?: string;
    // 揽收失败原因：有值时用「Exception 揽收失败原因」替换货物明细区块
    exceptionReason?: string;
    // 是否需要海外仓签字。地址错误/仓库关闭等场景现场无人可签，置 false 后只留司机签字栏
    shipperRequired?: boolean;
    driverName: string;
    driverPhone: string;
    licensePlate: string;
    // 落在单据的 Notes / Special Instructions 栏。
    // 揽收页传的是司机现场填的「揽收备注」
    remark?: string;
    shipperSignature: string | null;
    driverSignature: string | null;
    // 点「保存」才提交双方签字；直接返回不回调，浮层内画的一律作废
    onSave: (signatures: Record<PickUpBolSignatureRole, string | null>) => void;
}

export interface PickUpBolRef {
    /** 按当前单据内容出图（JPEG dataURL）。由页面在提交时调用，保证图与提交内容一致 */
    captureImage: () => Promise<string>;
}

// 单据本体的中英对照表头属于单据固定格式，不随 App 语言变，故硬编码；交互文案走 i18n
const BOL_BORDER = '1px solid #23233A';
const SIGNATURE_LABELS: Record<PickUpBolSignatureRole, string> = {
    shipper: 'Shipper Signature 海外仓签名:',
    driver: 'Driver Signature 司机签名:',
};
const SIGNATURE_ROLES: PickUpBolSignatureRole[] = ['shipper', 'driver'];
// 1x1 透明 PNG，作为图片取不到时的占位
const TRANSPARENT_PIXEL = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
const DRIVER_ONLY_SIGNATURE_ROLES: PickUpBolSignatureRole[] = ['driver'];
// 签名图在快照里的标记：出图时按它把签名排除在 html-to-image 之外，改由 canvas 合成
const SIGNATURE_IMAGE_FLAG = 'bolSignature';

// 画到指定尺寸的离屏 canvas
const paintToCanvas = (source: CanvasImageSource, width: number, height: number): HTMLCanvasElement => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (ctx) {
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(source, 0, 0, width, height);
    }
    return canvas;
};

// 逐级减半缩到目标尺寸。签名画板比单据上的签名框大 6 倍以上，一次直缩会把不足 1px 的
// 笔迹整条采样丢掉（iOS 上直接采空、macOS 上剩淡影，这正是签名"消失"的真正原因）；
// 每步只缩一半时双线性插值才是准确的，笔迹才留得住
const downscaleImage = (image: HTMLImageElement, targetWidth: number, targetHeight: number): CanvasImageSource => {
    let width = image.naturalWidth;
    let height = image.naturalHeight;
    let source: CanvasImageSource = image;
    while (width >= targetWidth * 2 && height >= targetHeight * 2) {
        width = Math.max(Math.round(width / 2), targetWidth);
        height = Math.max(Math.round(height / 2), targetHeight);
        source = paintToCanvas(source, width, height);
    }
    return source;
};

// 等解码完成再返回：只等 onload 的话，iOS 上 drawImage 有画出空白的可能
const loadImage = async (src: string): Promise<HTMLImageElement> => {
    const image = new Image();
    image.src = src;
    if (image.decode) {
        await image.decode();
        return image;
    }
    await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve();
        image.onerror = () => reject(new Error('Failed to load signature image'));
    });
    return image;
};

// BOL 提货单全屏浮层：展示单据 + 双方签字。单据 DOM 常驻（浮层关闭时移出屏幕），
// 出图由页面在提交时经 ref 触发，避免"改了原因还要进来点一次保存"这种无意义操作
const PickUpBol = forwardRef<PickUpBolRef, PickUpBolProps>(({
    visible,
    onClose,
    bolNumber,
    date,
    pickup,
    consignee,
    container,
    quantity,
    exceptionReason,
    shipperRequired = true,
    driverName,
    driverPhone,
    licensePlate,
    remark,
    shipperSignature,
    driverSignature,
    onSave,
}, ref) => {
    // 当前放大签字的角色，null 表示画板未打开
    const [signingRole, setSigningRole] = useState<PickUpBolSignatureRole | null>(null);
    // 浮层内的签字只是草稿：点「保存」才提交给页面，直接返回则整笔丢弃。
    // 否则"签了但没保存"也会刷新页面侧的签字基准（数量/单位/失败原因），
    // 导致下次带着别的基准进来被判定失效、要求重签
    const [draftSignatures, setDraftSignatures] = useState<Record<PickUpBolSignatureRole, string | null>>({
        shipper: shipperSignature,
        driver: driverSignature,
    });
    const documentRef = useRef<HTMLDivElement | null>(null);
    // 签名框的 DOM，出图后按它的实测位置把签名画回快照
    const signatureBoxRefs = useRef<Record<PickUpBolSignatureRole, HTMLDivElement | null>>({
        shipper: null,
        driver: null,
    });

    // 每次打开都以页面已保存的签字为起点，清掉上次未保存的残留。
    // 用 useLayoutEffect：普通 useEffect 在绘制后才跑，残留签字会闪现一帧。
    // 只依赖 visible：浮层开着时若跟着 props 重置，会把司机正在画的草稿抹掉
    useLayoutEffect(() => {
        if (visible) {
            setDraftSignatures({shipper: shipperSignature, driver: driverSignature});
            setSigningRole(null);
        }
    }, [visible]);

    // 把签名按签名框的实测位置画到快照上，缩放规则与页面上的 <img> 一致：等比缩进框内、居中
    const drawSignatures = async (canvas: HTMLCanvasElement, node: HTMLElement) => {
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }
        const nodeRect = node.getBoundingClientRect();
        // 取实测倍率而不是直接用 2：canvas 尺寸超限时库会整体缩过一道
        const ratio = canvas.width / nodeRect.width;
        for (const role of signatureRoles) {
            const dataUrl = signatures[role];
            const box = signatureBoxRefs.current[role];
            if (!dataUrl || !box) {
                continue;
            }
            // eslint-disable-next-line no-await-in-loop
            const image = await loadImage(dataUrl);
            const boxRect = box.getBoundingClientRect();
            const scale = Math.min(boxRect.width / image.naturalWidth, boxRect.height / image.naturalHeight);
            // 目标尺寸按快照的物理像素算，先逐级缩到位再原样贴，不让 drawImage 做大倍率缩放
            const width = Math.max(Math.round(image.naturalWidth * scale * ratio), 1);
            const height = Math.max(Math.round(image.naturalHeight * scale * ratio), 1);
            const x = Math.round((boxRect.left - nodeRect.left) * ratio + (boxRect.width * ratio - width) / 2);
            const y = Math.round((boxRect.top - nodeRect.top) * ratio + (boxRect.height * ratio - height) / 2);
            ctx.drawImage(downscaleImage(image, width, height), x, y, width, height);
        }
    };

    // 单据快照：白底必须显式给（默认透明，JPEG 会渲染成黑底导致签字看不见）；
    // 3 倍像素——签名框在成品里才有 300 来个像素宽，签名被压缩的倍率跟着降一档，
    // 细笔迹不至于糊成一片；质量 0.92 避免文字边缘振铃。
    // skipFonts：默认会遍历所有样式表内嵌 @font-face，而本页挂着 Google Fonts 与 Mapbox 两个跨域样式表，
    // 读 cssRules 必抛 SecurityError，退化路径还会把 CSS 里的 data URI 当相对路径请求出几十个 404
    useImperativeHandle(ref, () => ({
        captureImage: async () => {
            const node = documentRef.current;
            if (!node) {
                throw new Error('BOL document node is not mounted');
            }
            // 屏幕外渲染时若拿不到尺寸，截出来会是空白图，提前暴露而不是产出坏图
            if (!node.offsetWidth || !node.offsetHeight) {
                throw new Error(`BOL document has no layout size: ${node.offsetWidth}x${node.offsetHeight}`);
            }
            // 签名不交给 html-to-image：签名画板有 1300 多像素宽，单据上的签名框只有 100 来像素，
            // 交给它就是让 foreignObject 的栅格化一次性缩小 6 倍以上，不足 1px 的笔迹会被整条采样丢掉
            // ——WebKit 上表现为签名"消失"（Chromium 的缩图滤波质量高，所以安卓一直看着正常）。
            // 所以 filter 掉签名让框留空，出图后由 drawSignatures 逐级缩放画回原位
            const canvas = await toCanvas(node, {
                pixelRatio: 3,
                backgroundColor: '#ffffff',
                skipFonts: true,
                filter: (domNode) => !(domNode as HTMLElement)?.dataset?.[SIGNATURE_IMAGE_FLAG],
                // logo 已内联为 data URI、不产生请求；这两项是给日后可能加入单据的图片兜底：
                // 优先走缓存，取不到则用透明占位，绝不因一张图让整张单据出不来
                fetchRequestInit: {cache: 'force-cache'},
                imagePlaceholder: TRANSPARENT_PIXEL,
            });
            await drawSignatures(canvas, node);
            return canvas.toDataURL('image/jpeg', 0.92);
        },
    }));
    // 浮层可见时展示草稿；不可见时屏幕外的出图节点必须用已保存的签字，保证提交的图与页面状态一致
    const signatures: Record<PickUpBolSignatureRole, string | null> = visible
        ? draftSignatures
        : {shipper: shipperSignature, driver: driverSignature};
    const signatureRoles = shipperRequired ? SIGNATURE_ROLES : DRIVER_ONLY_SIGNATURE_ROLES;
    // 草稿与已保存内容有出入才算有未保存改动：既是「保存」的启用条件，也决定返回时要不要拦一下。
    // 保存不校验签字是否齐全——否则清空签字后按钮置灰，司机就没有撤销已保存签字的出口了；
    // 签齐才能提交这道关由页面底部的提交按钮（bolSigned）把守，这里不重复
    const dirty = draftSignatures.shipper !== shipperSignature || draftSignatures.driver !== driverSignature;

    const updateDraftSignature = (role: PickUpBolSignatureRole, dataUrl: string | null) => {
        setDraftSignatures((prev) => ({...prev, [role]: dataUrl}));
    };

    const handleSave = () => {
        onSave(draftSignatures);
        onClose();
    };

    // 双方签完误点返回等于白签一遍、还要重新找人签，先引导去点「保存」。
    // 主按钮回浮层（司机自己点保存），次按钮才是放弃返回
    const handleBack = () => {
        if (!dirty) {
            onClose();
            return;
        }
        Dialog.confirm({
            content: t('pages.pickUp.pickedUp.bolUnsavedContent'),
            confirmText: t('pages.pickUp.pickedUp.bolUnsavedGoSave'),
            cancelText: t('pages.pickUp.pickedUp.bolUnsavedLeave'),
            onCancel: onClose,
        });
    };

    // 竖分隔线：拍平成 grid 后，左列每一行都要补 border-right 才连成一条
    const columnBorder = (role: PickUpBolSignatureRole) => (
        shipperRequired && role === 'shipper' ? {borderRight: BOL_BORDER} : undefined
    );

    const renderSignatureLabel = (role: PickUpBolSignatureRole) => (
        <div key={`label-${role}`} className="px-1 pt-1.5 pb-1" style={{...columnBorder(role), color: '#5A5A6E'}}>
            {SIGNATURE_LABELS[role]}
        </div>
    );

    const renderSignatureBox = (role: PickUpBolSignatureRole) => {
        const signature = signatures[role];
        // 浮层里下方还有「清除」那一行垫着，快照里没有，得自己补回底部留白，
        // 否则签名框直接贴上区块下边框。pb-1.5 与上方标签的 pt-1.5 对称
        return (
            <div key={`box-${role}`} className={visible ? 'px-1' : 'px-1 pb-1.5'} style={columnBorder(role)}>
                <div
                    ref={(el) => {
                        signatureBoxRefs.current[role] = el;
                    }}
                    className="flex h-12 w-full items-center justify-center overflow-hidden"
                    style={{
                        backgroundColor: '#F1EEFB',
                        border: '1px solid #C9BEF0',
                    }}
                    onClick={() => setSigningRole(role)}
                >
                    {/* 这张 <img> 只管页面展示，出图时会被 filter 掉、改由 canvas 画（见 captureImage）。
                        maxWidth/maxHeight 等价于 background-size: contain，
                        且与 drawSignatures 里的等比缩放算法一致，两边呈现必须对得上 */}
                    {signature ? (
                        <img
                            src={signature}
                            alt=""
                            data-bol-signature="1"
                            style={{maxWidth: '100%', maxHeight: '100%'}}
                        />
                    ) : (
                        <span style={{fontSize: '9.5px', color: '#8A7FC5'}}>
                            {t('pages.pickUp.pickedUp.bolTapToSign')}
                        </span>
                    )}
                </div>
            </div>
        );
    };

    const renderSignatureClear = (role: PickUpBolSignatureRole) => (
        <div key={`clear-${role}`} className="px-1 pt-0.5 pb-1.5" style={columnBorder(role)}>
            <div
                className="text-right"
                style={{fontSize: '9.5px', color: '#8A8A93'}}
                onClick={() => updateDraftSignature(role, null)}
            >
                {t('pages.pickUp.pickedUp.bolClear')}
            </div>
        </div>
    );

    // 单据本体：浮层可见时嵌在浮层里，不可见时移出屏幕常驻，供提交时出图。
    // 不能用 display:none —— 那样元素没有布局尺寸，截出来是空白
    const documentNode = (
        <div
            ref={documentRef}
            className="w-full self-start bg-white"
            style={{maxWidth: '360px', border: '1.5px solid #23233A', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)'}}
        >
            <div className="p-3" style={{color: '#23233A', fontSize: '11px', lineHeight: 1.45}}>
                {/* 单据抬头 */}
                <div
                    className="mb-2.5 flex items-center justify-between pb-2"
                    style={{borderBottom: '2px solid #23233A'}}
                >
                    <div style={{fontSize: '12px', fontWeight: 800}}>Bill of Lading (BOL) 提货单</div>
                    <img src={SWIFTX_LOGO_BASE64} alt="SwiftX" style={{height: '16px'}}/>
                </div>

                {/* 窄屏自适应：同行时两端对齐；放不下则 Date 整块换行，
                    此时该行只剩它一个，space-between 会把它放在行首（左对齐）。
                    BOL 号自身超长则在本块内断行，两者都不会溢出屏幕 */}
                <div className="mb-2.5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                    <div className="min-w-0 break-all">
                        BOL No: <span className="font-bold" style={{borderBottom: BOL_BORDER}}>{bolNumber}</span>
                    </div>
                    <div className="shrink-0 whitespace-nowrap">
                        Date: <span className="font-bold" style={{borderBottom: BOL_BORDER}}>{date}</span>
                    </div>
                </div>

                {/* 提货方 / 收货方 */}
                <div className="mb-2.5 grid grid-cols-2" style={{border: BOL_BORDER}}>
                    <div className="p-1.5 font-bold" style={{borderRight: BOL_BORDER, borderBottom: BOL_BORDER}}>
                        Pickup Location 提货地址
                    </div>
                    <div className="p-1.5 font-bold" style={{borderBottom: BOL_BORDER}}>
                        Consignee 收货方
                    </div>
                    <div className="p-1.5" style={{borderRight: BOL_BORDER}}>
                        <b>站点ID:</b> {pickup.code}<br/>
                        <b>Address:</b> {pickup.address}<br/>
                        <b>Contact:</b> {pickup.contact}
                    </div>
                    <div className="p-1.5">
                        <b>HUB:</b> {consignee.code}<br/>
                        <b>Address:</b> {consignee.address}<br/>
                        <b>Contact:</b> {consignee.contact}
                    </div>
                </div>

                {/* 揽收成功展示货物明细；揽收失败没有数量单位，换成失败原因 */}
                {exceptionReason ? (
                    <div className="mb-2.5" style={{border: BOL_BORDER}}>
                        <div className="p-1.5 font-bold" style={{borderBottom: BOL_BORDER}}>
                            Exception 揽收失败原因
                        </div>
                        <div className="p-1.5 font-bold" style={{color: '#A8383A'}}>{exceptionReason}</div>
                    </div>
                ) : (
                    <div className="mb-2.5" style={{border: BOL_BORDER}}>
                        <div className="p-1.5 font-bold" style={{borderBottom: BOL_BORDER}}>
                            Shipment Details 货物明细
                        </div>
                        <div className="grid grid-cols-2 text-center">
                            <div className="p-1.5" style={{borderRight: BOL_BORDER, color: '#5A5A6E'}}>Container 容器类型</div>
                            <div className="p-1.5" style={{color: '#5A5A6E'}}>Qty 数量</div>
                            <div className="p-1.5 font-bold" style={{borderRight: BOL_BORDER, borderTop: BOL_BORDER}}>{container}</div>
                            <div className="p-1.5 font-bold" style={{borderTop: BOL_BORDER}}>{quantity}</div>
                        </div>
                    </div>
                )}

                {/* 司机信息与签字 */}
                <div className="mb-2.5" style={{border: BOL_BORDER}}>
                    <div className="p-1.5 font-bold" style={{borderBottom: BOL_BORDER}}>
                        Driver Information &amp; Signatures 司机信息和签字确认
                    </div>
                    <div className="flex gap-2 p-1.5" style={{borderBottom: BOL_BORDER}}>
                        <div className="flex-1">
                            <span style={{color: '#5A5A6E'}}>Driver Name:</span> <span className="font-bold">{driverName}</span>
                        </div>
                        <div className="flex-1">
                            <span style={{color: '#5A5A6E'}}>Driver Phone:</span> <span className="font-bold">{driverPhone}</span>
                        </div>
                    </div>
                    <div className="p-1.5" style={{borderBottom: BOL_BORDER}}>
                        <span style={{color: '#5A5A6E'}}>License Plate:</span> <span className="font-bold">{licensePlate}</span>
                    </div>
                    {/* 标签/画板/清除各占一行：grid 行高取两列较高者，
                        窄屏下标签换行也不会错位，且不必锁单行。
                        「清除」只在浮层里出现：单据快照是留底凭证，不能带操作按钮。
                        用 visible 而不是出图时 filter 掉——出图节点本来就是 visible=false 那一份，
                        它的布局与量到的签名框位置天然一致；filter 会让克隆体少一行、下方内容整体上移 */}
                    <div className={shipperRequired ? 'grid grid-cols-2' : 'grid grid-cols-1'}>
                        {signatureRoles.map(renderSignatureLabel)}
                        {signatureRoles.map(renderSignatureBox)}
                        {visible && signatureRoles.map(renderSignatureClear)}
                    </div>
                </div>

                {/* 备注 */}
                <div style={{border: BOL_BORDER}}>
                    <div className="p-1.5 font-bold" style={{borderBottom: BOL_BORDER}}>
                        Notes / Special Instructions 备注
                    </div>
                    <div className="p-1.5" style={{minHeight: '20px'}}>{remark || '-'}</div>
                </div>
            </div>
        </div>
    );

    if (!visible) {
        return (
            <div
                aria-hidden
                style={{position: 'fixed', left: '-10000px', top: 0, width: '360px', opacity: 0, pointerEvents: 'none'}}
            >
                {documentNode}
            </div>
        );
    }

    return (
        <div className="fixed inset-0 flex flex-col bg-white" style={{zIndex: 150}}>
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={handleBack}>{t('pages.pickUp.pickedUp.bolTitle')}</NavBar>
            </div>

            <div className="flex flex-1 justify-center overflow-y-auto p-3.5" style={{background: '#EDEBF3'}}>
                {documentNode}
            </div>

            <div {...getBottomNavBarContainerStyle('flex-0 px-4 py-2 mt-auto')}>
                <Button block color="primary" disabled={!dirty} onClick={handleSave}>
                    {t('pages.pickUp.pickedUp.bolSave')}
                </Button>
            </div>

            {/* 放大签字画板 */}
            <SignaturePad
                visible={signingRole !== null}
                title={signingRole ? SIGNATURE_LABELS[signingRole].replace(':', '') : ''}
                value={signingRole ? signatures[signingRole] : null}
                onConfirm={(dataUrl) => signingRole && updateDraftSignature(signingRole, dataUrl)}
                onClose={() => setSigningRole(null)}
            />
        </div>
    );
});

PickUpBol.displayName = 'PickUpBol';

export default PickUpBol;
